import { FormStatus, InfoCard, P, Pagination, Skeleton } from '@dnb/eufemia';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { createDummyEvent } from '@/dummyData';
import { useBackend } from '@/hooks/useBackend';
import { useEventFilterParams } from '@/hooks/useEventFilterParams';
import useSWRExpect404 from '@/hooks/useSWRExpect404';
import EventList from '@/pages/explorer/events/EventList';

const DUMMY_EVENTS = new Array(5).fill(null).map((_, id) => createDummyEvent(id));

export function PaginatedEvents() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterParams = useEventFilterParams();
  const page = Math.max(Number(searchParams.get('page')) || 0, 0);

  const swrExpect404 = useSWRExpect404();

  const {
    data: eventResponse,
    isLoading,
    error,
    mutate,
  } = useBackend(
    '/events',
    { params: { query: { page, ...filterParams } } },
    { keepPreviousData: true, ...swrExpect404 },
  );

  const onFavoriteChange = useCallback(
    (eventId: string, isFavorite: boolean) => {
      void mutate(
        (prev) => {
          if (!prev) return;
          const updated = structuredClone(prev);
          const event = updated.content.find((e) => e.id === eventId);
          if (event) {
            event.isFavorite = isFavorite;
          }
          return updated;
        },
        { revalidate: false },
      );
    },
    [mutate],
  );

  const events = eventResponse?.content ?? [];

  return (
    <Skeleton show={isLoading}>
      <div>
        <P bottom="medium" weight="medium">
          {eventResponse?.totalElements ?? 0} {eventResponse?.totalElements === 1 ? 'event' : 'events'}
        </P>
      </div>
      <div>
        {error ? (
          <FormStatus stretch text="Failed to fetch events." />
        ) : isLoading && !eventResponse ? (
          <EventList events={DUMMY_EVENTS} onFavoriteChange={() => {}} />
        ) : events.length === 0 ? (
          <InfoCard text="No events match the selected filters." />
        ) : (
          eventResponse && (
            <Pagination
              current_page={eventResponse.page + 1}
              on_change={({ pageNumber }) => {
                setSearchParams((prev) => {
                  prev.set('page', String(pageNumber - 1));
                  return prev;
                });
              }}
              page_count={eventResponse.totalPages}
            >
              <EventList events={events} onFavoriteChange={onFavoriteChange} />
            </Pagination>
          )
        )}
      </div>
    </Skeleton>
  );
}
